import { SphereTooltip } from "@components/common/sphere-tooltip";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { Button } from "@flat-ui";
import {
  generateCreditsFullRoute,
  generateCreditsPurchaseRedirectFullRoute,
} from "@router/router-helper";
import {
  CREDITS_BALANCE,
  CREDITS_DISPLAY_NAME,
} from "@src/constants/credit-constants";
import { runtimeConfig } from "@src/runtime-config";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { appStateSelector } from "@store/app/app-selector";
import { companyCreditBalanceSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { isProductionEnvironment } from "@utils/env-utils";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

interface Props {
  /** The currently selected credit package */
  selectedPackage?: FaroCoreApiTypes.StripeProduct;
}

/** Renders credit purchase button and redirect to checkout session */
export function CreditPurchase({
  selectedPackage,
}: Props): JSX.Element | undefined {
  const companyId = useCompanyId();
  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorContext();
  const companyCreditBalance: number = useAppSelector(
    companyCreditBalanceSelector
  );
  const { isAlphaTestingEnabled } = useAppSelector(appStateSelector);

  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  function onPurchaseClick(): void {
    // Track the event when the user clicks on Purchase tokens button
    trackEvent({
      name: WorkspaceEvents.initiatePurchaseCredits,
      props: { tokenPackage: selectedPackage ?? "-" },
    });

    if (!selectedPackage) {
      handleErrorWithToast({
        id: `onPurchaseClick-${Date.now().toString()}`,
        title: "Please select a token package",
        error: new Error("Package is not selected"),
      });

      return;
    }

    setIsCheckoutLoading(true);

    // Save current balance in local storage to compare after purchase
    // The value is used to show indication if newly purchased token not added quickly before redirection
    localStorage.setItem(CREDITS_BALANCE, companyCreditBalance.toString());

    // Payload for the POST request to create and redirect to a Stripe checkout session
    const payload = {
      stripeProductId: selectedPackage.id,
      workspaceId: companyId,
      successUrl: generateCreditsPurchaseRedirectFullRoute(companyId),
      cancelUrl: generateCreditsFullRoute(companyId),
      // TODO: Remove this coupon https://faro01.atlassian.net/browse/ST-3530
      // Coupon created in stripe dashboard and applied to all package
      // In future marketing team can publish coupons and user can redeemed in the checkout page
      coupon: "BETA50",
      // eslint-disable-next-line @typescript-eslint/naming-convention -- defined by backend
      relyOnUserForBillingEmail: true,
    };

    // Create a form to submit the payload to the backend
    const form = document.createElement("form");
    form.action = `${runtimeConfig.urls.apiBaseUrl}/stripe/product/checkout-session`;
    form.method = "POST";
    form.style.display = "none";

    Object.entries(payload).forEach(([name, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = String(value);
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  if (!isAlphaTestingEnabled || isProductionEnvironment) {
    return undefined;
  }

  return (
    <SphereTooltip
      title="Please select a package to purchase."
      shouldShowTooltip={!selectedPackage}
    >
      <Button
        fullWidth
        onClick={onPurchaseClick}
        disabled={!selectedPackage}
        isLoading={isCheckoutLoading}
      >
        Purchase {CREDITS_DISPLAY_NAME}
      </Button>
    </SphereTooltip>
  );
}
